// Voice command parser for Voice Chess.
// Pure functions, no imports — kept node-testable (see frontend/test.js).
//
// parseCommand(text) turns a spoken/typed phrase into an intent object.
// resolveMove(intent, legalMoves) matches a move intent against chess.js
// verbose legal moves and returns exactly one move, or an error.

// Words that speech recognition commonly substitutes for files a–h.
const FILE_WORDS = {
  a: 'a', alpha: 'a',
  b: 'b', be: 'b', bee: 'b', bravo: 'b',
  c: 'c', see: 'c', sea: 'c', si: 'c', charlie: 'c',
  d: 'd', de: 'd', dee: 'd', delta: 'd',
  e: 'e', echo: 'e',
  f: 'f', ef: 'f', eff: 'f', foxtrot: 'f',
  g: 'g', gee: 'g', golf: 'g',
  h: 'h', aitch: 'h', age: 'h', hotel: 'h',
};

// Words that map to ranks 1–8 (only applied directly after a file word).
const RANK_WORDS = {
  1: '1', one: '1', won: '1',
  2: '2', two: '2', too: '2', to: '2',
  3: '3', three: '3', tree: '3',
  4: '4', four: '4', for: '4', fore: '4',
  5: '5', five: '5',
  6: '6', six: '6',
  7: '7', seven: '7',
  8: '8', eight: '8', ate: '8',
};

// Whole-word homophones for full squares.
const SQUARE_HOMOPHONES = {
  before: 'b4',
  befour: 'b4',
  gate: 'g8',
};

const PIECE_WORDS = {
  knight: 'n', night: 'n', nite: 'n', horse: 'n',
  bishop: 'b',
  rook: 'r', rock: 'r', brook: 'r',
  queen: 'q',
  king: 'k',
  pawn: 'p', pon: 'p', pond: 'p', paun: 'p',
};

const PIECE_NAMES = { p: 'pawn', n: 'knight', b: 'bishop', r: 'rook', q: 'queen', k: 'king' };

const CAPTURE_WORDS = new Set(['takes', 'take', 'captures', 'capture', 'x']);

function normalize(text) {
  return String(text)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
}

// One pass over the raw words extracting squares and piece names.
// Returns { words, squares, pieces, orderedTokens } where orderedTokens
// preserves relative order of squares/pieces/keywords for move parsing.
function extractTokens(rawWords) {
  const squares = [];
  const pieces = [];
  const words = [];
  const ordered = [];

  for (let i = 0; i < rawWords.length; i++) {
    const w = rawWords[i];

    if (/^[a-h][1-8]$/.test(w)) {
      squares.push(w);
      ordered.push({ type: 'square', value: w });
      continue;
    }
    if (SQUARE_HOMOPHONES[w]) {
      squares.push(SQUARE_HOMOPHONES[w]);
      ordered.push({ type: 'square', value: SQUARE_HOMOPHONES[w] });
      continue;
    }
    // file word followed by a rank word → square ("b six" → b6)
    if (FILE_WORDS[w] && i + 1 < rawWords.length && RANK_WORDS[rawWords[i + 1]]) {
      const sq = FILE_WORDS[w] + RANK_WORDS[rawWords[i + 1]];
      squares.push(sq);
      ordered.push({ type: 'square', value: sq });
      i++;
      continue;
    }
    if (PIECE_WORDS[w]) {
      pieces.push(PIECE_WORDS[w]);
      ordered.push({ type: 'piece', value: PIECE_WORDS[w] });
      continue;
    }
    words.push(w);
    ordered.push({ type: 'word', value: w });
  }

  return { words, squares, pieces, ordered };
}

function has(words, ...targets) {
  return targets.some((t) => words.includes(t));
}

function hasPhrase(words, ...phrase) {
  for (let i = 0; i + phrase.length <= words.length; i++) {
    if (phrase.every((p, j) => words[i + j] === p)) return true;
  }
  return false;
}

export function parseCommand(text) {
  const rawWords = normalize(text);
  if (rawWords.length === 0) return null;

  // --- Non-move commands checked on raw words first (before "queen" etc.
  // get interpreted as pieces). ---

  // close the help panel — must be checked before "help" so
  // "close help" / "hide the help" don't re-open it
  if (has(rawWords, 'close', 'dismiss') ||
      (has(rawWords, 'hide') && has(rawWords, 'help')) ||
      hasPhrase(rawWords, 'never', 'mind')) {
    return { type: 'close' };
  }

  // help
  if (has(rawWords, 'help', 'commands') || hasPhrase(rawWords, 'what', 'can', 'i', 'say')) {
    return { type: 'help' };
  }

  // mic mute/unmute
  if (hasPhrase(rawWords, 'stop', 'listening') || has(rawWords, 'mute')) {
    return { type: 'listen', on: false };
  }
  if (hasPhrase(rawWords, 'start', 'listening') || has(rawWords, 'unmute') ||
      hasPhrase(rawWords, 'wake', 'up')) {
    return { type: 'listen', on: true };
  }

  // eval bar toggle
  if (has(rawWords, 'eval', 'evaluation', 'engine')) {
    if (has(rawWords, 'off', 'hide', 'stop', 'disable')) return { type: 'eval', on: false };
    if (has(rawWords, 'on', 'show', 'start', 'enable')) return { type: 'eval', on: true };
    return { type: 'eval', on: null }; // toggle
  }

  // reset / new game
  if (has(rawWords, 'reset', 'restart') || hasPhrase(rawWords, 'new', 'game') ||
      hasPhrase(rawWords, 'start', 'over') || hasPhrase(rawWords, 'starting', 'position')) {
    return { type: 'reset' };
  }

  // flip board
  if (has(rawWords, 'flip', 'rotate')) return { type: 'flip' };

  // undo / redo (with common mishearings: "undue", "and do", "on do")
  if (has(rawWords, 'undo', 'undue') || hasPhrase(rawWords, 'take', 'back') ||
      hasPhrase(rawWords, 'go', 'back') || hasPhrase(rawWords, 'and', 'do') ||
      hasPhrase(rawWords, 'on', 'do') || hasPhrase(rawWords, 'un', 'do')) {
    return { type: 'undo' };
  }
  if (has(rawWords, 'redo') || hasPhrase(rawWords, 'go', 'forward') ||
      hasPhrase(rawWords, 're', 'do')) {
    return { type: 'redo' };
  }

  // clear the whole board
  if (has(rawWords, 'clear', 'empty', 'wipe') && has(rawWords, 'board')) {
    return { type: 'clearBoard' };
  }

  // castling — checked before move parsing so "castle" is never a rook move
  if (has(rawWords, 'castle', 'castles', 'castling')) {
    const queenside = has(rawWords, 'queenside', 'queen', 'long');
    return { type: 'castle', side: queenside ? 'queen' : 'king' };
  }

  // whose turn it is (lets you play both sides / skip a move)
  if (has(rawWords, 'white', 'black') && has(rawWords, 'move', 'turn', 'moves', 'play')) {
    // "white to move", "black's turn", "let white play"
    if (!has(rawWords, 'place', 'put', 'add')) {
      return { type: 'turn', color: has(rawWords, 'white') ? 'w' : 'b' };
    }
  }

  // --- Structured tokens for placement / removal / loading / moves ---
  const { words, squares, pieces, ordered } = extractTokens(rawWords);

  // place a piece: "place white knight on c3", "put a queen on d4"
  if (has(words, 'place', 'put', 'add')) {
    if (pieces.length >= 1 && squares.length >= 1) {
      const color = has(words, 'black') ? 'b' : 'w';
      return { type: 'place', color, piece: pieces[0], square: squares[0] };
    }
    return null;
  }

  // remove a piece: "remove d4", "delete the piece on e5", "clear d4"
  if (has(words, 'remove', 'delete', 'clear') || hasPhrase(words, 'take', 'off')) {
    if (squares.length >= 1) return { type: 'remove', square: squares[0] };
    return null;
  }

  // load a named position: "load the italian game", "set up a rook endgame"
  if (has(words, 'load', 'setup') || hasPhrase(words, 'set', 'up') ||
      has(words, 'position')) {
    return { type: 'load', words: rawWords };
  }

  // force a move, rules be damned: "sudo d1 to h3"
  // ("pseudo" / "sue do" are what speech recognition hears for "sudo")
  if (has(words, 'sudo', 'pseudo', 'force', 'override') || hasPhrase(words, 'sue', 'do')) {
    if (squares.length === 2) {
      return { type: 'forceMove', from: squares[0], to: squares[1] };
    }
    return { type: 'forceMove', from: null, to: null };
  }

  // --- Move parsing ---
  const capture = rawWords.some((w) => CAPTURE_WORDS.has(w));

  // promotion: "e8 promote to queen", "promote to knight"
  let promotion = null;
  const promoteIdx = rawWords.findIndex((w) => w === 'promote' || w === 'promotion' || w === 'promotes');
  if (promoteIdx !== -1) {
    for (let i = promoteIdx + 1; i < rawWords.length; i++) {
      const p = PIECE_WORDS[rawWords[i]];
      if (p && p !== 'p' && p !== 'k') { promotion = p; break; }
    }
  }

  // Piece tokens that name a promotion target aren't the moving piece.
  let movePieces = pieces;
  if (promotion) movePieces = pieces.filter((p) => p !== promotion);

  if (squares.length === 2) {
    // "e2 to e4", "knight b1 to c3"
    return {
      type: 'move',
      from: squares[0],
      to: squares[1],
      piece: movePieces[0] || null,
      capture,
      promotion,
    };
  }

  if (squares.length === 1) {
    // "e4", "knight to b6", "rook takes d5", "takes d5"
    return {
      type: 'move',
      from: null,
      to: squares[0],
      piece: movePieces[0] || null,
      capture,
      promotion,
    };
  }

  return null;
}

// Match a move intent against chess.js verbose legal moves.
// Returns { move } on a unique match, or { error, options? }.
export function resolveMove(intent, legalMoves) {
  if (intent.type === 'castle') {
    const san = intent.side === 'queen' ? 'O-O-O' : 'O-O';
    const m = legalMoves.find((mv) => mv.san.replace(/[+#]/g, '') === san);
    return m ? { move: m } : { error: `Castling ${intent.side}side is not legal here.` };
  }

  let matches = legalMoves;
  if (intent.to) matches = matches.filter((m) => m.to === intent.to);
  if (intent.from) matches = matches.filter((m) => m.from === intent.from);

  if (intent.piece) {
    matches = matches.filter((m) => m.piece === intent.piece);
  } else if (!intent.from) {
    // Bare square ("e4") means a pawn move in SAN; fall back to any piece
    // if no pawn can reach it and the move is otherwise unique.
    const pawnMatches = matches.filter((m) => m.piece === 'p');
    if (pawnMatches.length > 0) matches = pawnMatches;
  }

  if (intent.capture) {
    const captures = matches.filter((m) => m.flags.includes('c') || m.flags.includes('e'));
    if (captures.length > 0) matches = captures;
  }

  // Collapse promotion variants to the requested (or default queen) piece.
  if (matches.some((m) => m.promotion)) {
    const target = intent.promotion || 'q';
    matches = matches.filter((m) => !m.promotion || m.promotion === target);
  }

  if (matches.length === 1) return { move: matches[0] };
  if (matches.length === 0) {
    const what = intent.piece ? PIECE_NAMES[intent.piece] : 'piece';
    return { error: `No legal move: ${what} to ${intent.to || '?'}.` };
  }
  return {
    error: 'Ambiguous — say the starting square, e.g. "' +
      `${PIECE_NAMES[matches[0].piece]} ${matches[0].from} to ${matches[0].to}".`,
    options: matches.map((m) => m.san),
  };
}

export { PIECE_NAMES };
